/**
 * Capture controller (F1 host).
 *
 * Orchestrates the full host-capture pipeline:
 *   1. Acquire a MediaStream via getUserMedia (mic) or getDisplayMedia (tab),
 *      requesting echoCancellation / noiseSuppression / autoGainControl.
 *   2. Feed it through AudioWorkletCapture → 16 kHz mono PCM16 frames.
 *   3. Apply light client-side VAD (energy threshold) to skip obvious silence.
 *   4. Send surviving frames as BINARY over the host WS uplink.
 *
 * Connection to the host WS (`/ws/host/{session_id}?token=…`) is owned here so
 * that start/stop cleanly opens/closes both the audio graph and the socket.
 */

import { WsClient, WS_BASE, type WsStatus } from '@/shared/ws/wsClient';
import { AudioWorkletCapture, type PcmFrame } from '@/features/capture/audioWorklet';
import {
  supportsDisplayMediaAudio,
  supportsUserMedia,
  toPermissionError,
  type CaptureSource,
} from '@/features/capture/audioDevices';
import type { EventEnvelopeV1 } from '@/shared/models/events';

/**
 * Media constraints mandated by PRD §3.1 for host capture.
 *
 * Anti-echo defense-in-depth (RF08 / PRD §4):
 *   1st line — browser Acoustic Echo Cancellation. `echoCancellation`,
 *   `noiseSuppression` and `autoGainControl` MUST all be `true` here so the
 *   browser removes locally-played TTS from the captured mic signal before it
 *   ever reaches the uplink. This is the cheapest and most effective guard for
 *   the open-speaker loop.
 *   2nd line — the server keeps a window of recently emitted TTS text and drops
 *   any transcribed segment that matches it (semantic suppression), catching
 *   echo that survives AEC (e.g. tab-audio capture, which cannot use AEC).
 * A headset remains the recommended physical mitigation (PRD §11 risk table).
 */
const AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  channelCount: 1,
};

/**
 * RMS gate for the light client-side VAD. Frames below this energy for longer
 * than the hangover window are dropped. Deliberately conservative — the server
 * runs silero-vad for precise cuts (PRD §8 #2); this only trims dead air.
 */
const VAD_RMS_THRESHOLD = 0.01;
/** Keep sending for this many silent frames after speech, to avoid clipping tails. */
const VAD_HANGOVER_FRAMES = 12; // ~480 ms at 40 ms/frame

export interface CaptureControllerOptions {
  sessionId: string;
  /** JWT of the session owner — required by the uplink (RNF09). */
  token: string;
  /** Skip obvious silence with the energy-threshold VAD (default true). */
  vadEnabled?: boolean;
  onStatus?: (status: WsStatus) => void;
  onEvent?: (event: EventEnvelopeV1) => void;
  onError?: (message: string) => void;
}

export class CaptureController {
  private readonly opts: CaptureControllerOptions;
  private readonly vadEnabled: boolean;

  private ws: WsClient | null = null;
  private capture: AudioWorkletCapture | null = null;
  private stream: MediaStream | null = null;
  private silentFrames = 0;
  private running = false;

  constructor(options: CaptureControllerOptions) {
    this.opts = options;
    this.vadEnabled = options.vadEnabled ?? true;
  }

  get isRunning(): boolean {
    return this.running;
  }

  /** Acquire audio, open the uplink, and begin streaming PCM frames. */
  async start(source: CaptureSource): Promise<void> {
    if (this.running) return;

    const stream = await this.acquireStream(source);
    this.stream = stream;

    // Open the host WS: uplink (binary audio) + downlink (JSON events).
    const query = `?token=${encodeURIComponent(this.opts.token)}`;
    const ws = new WsClient({
      url: `${WS_BASE}/ws/host/${encodeURIComponent(this.opts.sessionId)}${query}`,
      onStatus: this.opts.onStatus,
      onError: () => this.opts.onError?.('Erro na conexão WebSocket do host.'),
    });
    if (this.opts.onEvent) {
      ws.onEvent(this.opts.onEvent);
    }
    ws.connect();
    this.ws = ws;

    const capture = new AudioWorkletCapture();
    await capture.start(stream, (frame) => this.handleFrame(frame));
    this.capture = capture;

    // If the user ends screen-share from the browser chrome, stop cleanly.
    stream.getAudioTracks().forEach((track) => {
      track.addEventListener('ended', () => {
        void this.stop();
      });
    });

    this.running = true;
  }

  /** Stop streaming, close the socket, and release the media stream. */
  async stop(): Promise<void> {
    this.running = false;
    if (this.capture) {
      await this.capture.stop();
      this.capture = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.silentFrames = 0;
  }

  private handleFrame(frame: PcmFrame): void {
    if (!this.ws) return;
    if (this.vadEnabled && this.isSilence(frame.rms)) return;
    this.ws.sendBinary(frame.pcm);
  }

  /** Energy-threshold VAD with a hangover window to preserve speech tails. */
  private isSilence(rms: number): boolean {
    if (rms >= VAD_RMS_THRESHOLD) {
      this.silentFrames = 0;
      return false;
    }
    this.silentFrames += 1;
    return this.silentFrames > VAD_HANGOVER_FRAMES;
  }

  private async acquireStream(source: CaptureSource): Promise<MediaStream> {
    try {
      if (source.kind === 'tab') {
        if (!supportsDisplayMediaAudio()) {
          throw new DOMException('unsupported', 'NotSupportedError');
        }
        // Video is required by getDisplayMedia; we discard the video track and
        // keep only audio. Best supported in Chromium (Chrome/Edge).
        const display = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
          },
        });
        const audioTracks = display.getAudioTracks();
        if (audioTracks.length === 0) {
          display.getTracks().forEach((t) => t.stop());
          throw new DOMException('no audio track', 'NotFoundError');
        }
        display.getVideoTracks().forEach((t) => {
          t.stop();
          display.removeTrack(t);
        });
        return display;
      }

      if (!supportsUserMedia()) {
        throw new DOMException('unsupported', 'NotSupportedError');
      }
      const audio: MediaTrackConstraints = { ...AUDIO_CONSTRAINTS };
      if (source.deviceId) {
        audio.deviceId = { exact: source.deviceId };
      }
      return await navigator.mediaDevices.getUserMedia({ audio });
    } catch (err) {
      throw toPermissionError(err);
    }
  }
}
